import { Grid } from '@mui/material';
import Box from '@mui/material/Box';
import { addLocationApi, addLocationsApi, getLocationListApi, updateLocationApi } from 'SupabseHandler';
import {
    businessData as businessDataAtom,
    locationData as locationDataAtom,
    locationDataList,
    locationList as locationListAtom,
    selectedLocation as selectedLocationAtom,
} from 'atoms';
import Button from 'common/Button';
import ButtonSecondary from 'common/ButtonSecondary';
import FormModal from 'common/FormModal';
import FormStyle from 'common/FormStyle';
import ButtonLoader from 'common/Loaders/ButtonLoader';
import StickyActionFooter from 'common/StickyActionFooter';
import { deleteLocationApi } from 'SupabseHandler';
import { useRecoilState } from 'recoil';
import { useAuth } from 'context/AuthContext';

import React, { useEffect, useState } from 'react';

import LocationFormData from './LocationFormData';

const AddLocationPopup = ({ open, onClose }) => {
    const { token } = useAuth();
    const [formData, setFormData] = useRecoilState(locationDataList);
    const [selectedLocation, setSelectedLocation] = useRecoilState(selectedLocationAtom);
    const [business, setBusiness] = useRecoilState(businessDataAtom);
    const [locationList, setLocationList] = useRecoilState(locationListAtom);
    const [formStatus, setFormStatus] = useState('add')
    const [initialFormState, setInitialFormState] = useState(null);
    const [attemptedSubmit, setAttemptedSubmit] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (open) {
            setInitialFormState(JSON.stringify(formData)); // Store initial form data
            setAttemptedSubmit(false); // Reset form submission attempt
            setError(''); // Clear any previous errors
        }
    }, [open]);

    useEffect(() => {
        if (formData?.length > 0 && selectedLocation?.id) {
            setFormStatus('edit');
        } else {
            setFormStatus('add');
        }
    }, [formData, selectedLocation]);
    
    // Function to validate if a phone number is complete
    const isValidPhoneNumber = (phoneNumber) => {
        if (!phoneNumber) return false;
        
        // Remove all non-digit characters
        const digitsOnly = phoneNumber.replace(/\D/g, '');
        
        // Check if it's a complete US phone number (11 digits with country code)
        return digitsOnly.length === 11;
    };

    // Function to validate if all mandatory fields are filled
    const isFormValid = () => {
        // Check if we have location data
        if (!formData || formData.length === 0) return false;
        
        // Validate required fields for the first location - only name and phone are required
        const location = formData[0];
        const isLocationValid = 
            !!location?.location_name?.trim() && 
            isValidPhoneNumber(location?.location_phone_no);
        
        return isLocationValid;
    };

    const handleClose = () => {
        const hasUnsavedChanges = JSON.stringify(formData) !== initialFormState;

        if (hasUnsavedChanges) {
            const confirmExit = window.confirm('You have unsaved changes. Do you really want to close?');
            if (!confirmExit) return;
        }

        setIsSubmitting(false);
        closePopup();
    };
    const getLocationList = async () => {
        try {
            if (!token) {
                console.error('No authentication token found');
                setLocationList([]);
                return;
            }
            const data = await getLocationListApi(business?.id, token);
            // Ensure data is always an array
            setLocationList(Array.isArray(data) ? data : []);
        } catch (err) {
            console.error('Error fetching location list:', err);
            // Always set to an array, even on error
            setLocationList([]);
        }
    };
    const addLocation = async () => {
        try {
            if (!token) {
                console.error('No authentication token found');
                return;
            }
            const data = await addLocationApi(formData[0], business?.id, token);
            getLocationList();
            
        } catch (err) {
 
        }
    };
    const updateLocation = async () => {
        try {
            if (!token) {
                console.error('No authentication token found');
                return;
            }
            const data = await updateLocationApi(formData[0], selectedLocation, token);
            getLocationList();
            
        } catch (err) {}
    };

    const handleDeleteLocation = async () => {
        let confirmDelete = window.confirm('Are you sure you want to delete this location?');

        if (!confirmDelete) {
            return;
        }
        try {
            if (!token) {
                console.error('No authentication token found');
                return;
            }
            const data = await deleteLocationApi(selectedLocation.id, token);
            getLocationList();
            closePopup();
        } catch (err) {
            console.error('Error deleting location:', err);
        }
    };
    const handleSubmit = async (e) => {
        try {
            e.preventDefault();
            setAttemptedSubmit(true);
            
            if (!isFormValid()) {
                setError('Please fill in all required fields.');
                return;
            }
            
            setIsSubmitting(true);
            
            if (selectedLocation?.id) {
                await updateLocation();
            } else {
                await addLocation();
            }
            closePopup()
        } catch (error) {
            console.error('Error in handleSubmit:', error);
        } finally {
            setIsSubmitting(false);
        }
    };
    const closePopup = ()=>{
        setFormData([])
        onClose()
        setFormStatus('add')
        setAttemptedSubmit(false)
        setError('')
        setIsSubmitting(false)
    }

    return (
        <FormModal
            open={open}
            onClose={handleClose}
            title={formStatus === 'add' ? 'Add a Office Location' : 'Edit Office Location'}
            subText={formStatus === 'add' 
                ? 'Add a new office location for your business. Fill out the form below with the location details.'
                : 'Update the office location information below.'
            }
            stickyFooter={
                <StickyActionFooter
                    onCancel={closePopup}
                    onSave={handleSubmit}
                    cancelText="Cancel"
                    saveText={formStatus === 'add' ? 'Add Location' : 'Save'}
                    isLoading={isSubmitting}
                    disabled={!isFormValid()}
                    showDelete={formStatus === 'edit'}
                    onDelete={handleDeleteLocation}
                />
            }
        >
            <FormStyle>
                {error && <div className={'text-red-500 mt-2 mb-4'}>{error}</div>}
                <LocationFormData></LocationFormData>
            </FormStyle>
        </FormModal>
    );
};

export default AddLocationPopup;
